import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "mb-6 flex items-center gap-3" }
const _hoisted_3 = { class: "p-2 bg-[#22282F]/10 rounded-lg" }
const _hoisted_4 = { class: "flex gap-1 border-b border-gray-200 mb-6" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-3" }
const _hoisted_7 = ["value"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "py-16 text-center text-gray-400"
}
const _hoisted_10 = {
  key: 1,
  class: "py-16 text-center text-red-500"
}
const _hoisted_11 = { class: "bg-white rounded-xl border border-gray-200 overflow-hidden" }
const _hoisted_12 = { class: "w-full text-sm" }
const _hoisted_13 = { class: "px-4 py-3 text-gray-500 whitespace-nowrap" }
const _hoisted_14 = { class: "px-4 py-3 whitespace-nowrap" }
const _hoisted_15 = { class: "px-4 py-3 whitespace-nowrap" }
const _hoisted_16 = { class: "inline-block px-2 py-0.5 rounded-full text-xs bg-[#1d2a5e]/10 text-[#1d2a5e]" }
const _hoisted_17 = { class: "px-4 py-3 text-gray-700 whitespace-pre-line" }
const _hoisted_18 = { key: 0 }
const _hoisted_19 = { class: "flex items-center justify-between mt-4 text-sm text-gray-500" }
const _hoisted_20 = { class: "flex items-center gap-2" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = ["disabled"]
const _hoisted_23 = {
  key: 1,
  class: "max-w-2xl"
}
const _hoisted_24 = {
  key: 0,
  class: "py-16 text-center text-gray-400"
}
const _hoisted_25 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_26 = { class: "flex flex-wrap gap-2" }
const _hoisted_27 = ["value", "checked", "onChange"]
const _hoisted_28 = { class: "flex items-center gap-3 pt-2" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = {
  key: 0,
  class: "text-emerald-600 text-sm flex items-center gap-1"
}
const _hoisted_31 = {
  key: 1,
  class: "text-red-500 text-sm"
}

import { ref, computed, reactive, onMounted } from 'vue'
import { MessageSquare, Download, Check } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'

interface Choice { value: string; label: string }
interface Suggestion {
  id: number
  profil: string
  profil_label: string
  categorie: string
  categorie_label: string
  message: string
  created_at: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FeedbackView',
  setup(__props) {

const activeTab = ref<'suggestions' | 'config'>('suggestions')

const categories = ref<Choice[]>([])
const joursLabels = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

// ---- Suggestions ----
const items = ref<Suggestion[]>([])
const count = ref(0)
const page = ref(1)
const pageSize = ref(25)
const loading = ref(false)
const error = ref<string | null>(null)
const exporting = ref(false)

const filters = reactive({ profil: '', categorie: '', from: '', to: '' })

const totalPages = computed(() => Math.max(1, Math.ceil(count.value / pageSize.value)))

function buildQuery(includePaging = true): string {
  const p = new URLSearchParams()
  if (filters.profil) p.set('profil', filters.profil)
  if (filters.categorie) p.set('categorie', filters.categorie)
  if (filters.from) p.set('from', filters.from)
  if (filters.to) p.set('to', filters.to)
  if (includePaging) {
    p.set('page', String(page.value))
    p.set('page_size', String(pageSize.value))
  }
  return p.toString()
}

async function fetchSuggestions() {
  loading.value = true
  error.value = null
  try {
    const data = await apiFetch<{ count: number; results: Suggestion[]; page: number; page_size: number }>(
      `/api/admin/feedback/suggestions/?${buildQuery()}`
    )
    items.value = data.results
    count.value = data.count
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    loading.value = false
  }
}

function applyFilters() { page.value = 1; fetchSuggestions() }
function resetFilters() {
  filters.profil = ''; filters.categorie = ''; filters.from = ''; filters.to = ''
  page.value = 1; fetchSuggestions()
}
function nextPage() { if (page.value < totalPages.value) { page.value++; fetchSuggestions() } }
function prevPage() { if (page.value > 1) { page.value--; fetchSuggestions() } }

async function exportCsv() {
  exporting.value = true
  try {
    const res = await fetch(`${API_BASE_URL}/api/admin/feedback/suggestions/export/?${buildQuery(false)}`, {
      credentials: 'include',
    })
    if (!res.ok) throw new Error(`Erreur ${res.status}`)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `suggestions-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'export."
  } finally {
    exporting.value = false
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ---- Configuration ----
const configLoading = ref(false)
const saving = ref(false)
const configSaved = ref(false)
const configError = ref<string | null>(null)
const form = reactive<{ teams_url: string; horaire_debut: string; horaire_fin: string; jours: number[]; timezone: string }>({
  teams_url: '', horaire_debut: '08:30', horaire_fin: '18:00', jours: [0, 1, 2, 3, 4], timezone: 'Africa/Porto-Novo',
})

function toggleJour(idx: number) {
  const i = form.jours.indexOf(idx)
  if (i >= 0) form.jours.splice(i, 1)
  else form.jours.push(idx)
}

async function fetchConfig() {
  configLoading.value = true
  try {
    const data = await apiFetch<any>('/api/admin/feedback/config/')
    form.teams_url = data.teams_url || ''
    form.horaire_debut = data.horaire_debut || '08:30'
    form.horaire_fin = data.horaire_fin || '18:00'
    form.jours = Array.isArray(data.jours) ? data.jours : [0, 1, 2, 3, 4]
    form.timezone = data.timezone || 'Africa/Porto-Novo'
    if (Array.isArray(data.categories)) categories.value = data.categories
  } catch (e) {
    configError.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    configLoading.value = false
  }
}

async function saveConfig() {
  saving.value = true
  configSaved.value = false
  configError.value = null
  try {
    await apiFetch('/api/admin/feedback/config/', {
      method: 'PUT',
      body: JSON.stringify({
        teams_url: form.teams_url,
        horaire_debut: form.horaire_debut,
        horaire_fin: form.horaire_fin,
        jours: [...form.jours].sort((a, b) => a - b),
        timezone: form.timezone,
      }),
    })
    configSaved.value = true
    setTimeout(() => { configSaved.value = false }, 2500)
  } catch (e) {
    configError.value = e instanceof Error ? e.message : "Échec de l'enregistrement."
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  fetchConfig()      // charge aussi les listes profils/catégories pour les filtres
  fetchSuggestions()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(MessageSquare), { class: "w-7 h-7 text-[#22282F]" })
          ]),
          _cache[10] || (_cache[10] = _createElementVNode("div", null, [
            _createElementVNode("h1", { class: "text-2xl sm:text-3xl font-bold text-gray-900" }, "Feedback"),
            _createElementVNode("p", { class: "text-sm text-gray-500" }, "Suggestions anonymes reçues et configuration du support.")
          ], -1))
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = 'suggestions')),
            class: _normalizeClass(['px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
            activeTab.value === 'suggestions' ? 'border-[#22282F] text-[#22282F]' : 'border-transparent text-gray-500 hover:text-gray-800'])
          }, "Suggestions", 2),
          _createElementVNode("button", {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (activeTab.value = 'config')),
            class: _normalizeClass(['px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
            activeTab.value === 'config' ? 'border-[#22282F] text-[#22282F]' : 'border-transparent text-gray-500 hover:text-gray-800'])
          }, "Configuration support", 2)
        ]),
        (activeTab.value === 'suggestions')
          ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", null, [
                  _cache[11] || (_cache[11] = _createElementVNode("label", { class: "block text-xs font-semibold text-gray-600 mb-1" }, "Profil (poste)", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.profil) = $event)),
                    type: "text",
                    placeholder: "ex. Chef de Service",
                    class: "border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[180px]",
                    onKeyup: _withKeys(applyFilters, ["enter"])
                  }, null, 544), [
                    [_vModelText, filters.profil]
                  ])
                ]),
                _createElementVNode("div", null, [
                  _cache[13] || (_cache[13] = _createElementVNode("label", { class: "block text-xs font-semibold text-gray-600 mb-1" }, "Catégorie", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.categorie) = $event)),
                    class: "border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[160px]"
                  }, [
                    _cache[12] || (_cache[12] = _createElementVNode("option", { value: "" }, "Toutes", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (c) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: c.value,
                        value: c.value
                      }, _toDisplayString(c.label), 9, _hoisted_7))
                    }), 128))
                  ], 512), [
                    [_vModelSelect, filters.categorie]
                  ])
                ]),
                _createElementVNode("div", null, [
                  _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-xs font-semibold text-gray-600 mb-1" }, "Du", -1)),
                  _withDirectives(_createElementVNode("input", {
                    type: "date",
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.from) = $event)),
                    class: "border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  }, null, 512), [
                    [_vModelText, filters.from]
                  ])
                ]),
                _createElementVNode("div", null, [
                  _cache[15] || (_cache[15] = _createElementVNode("label", { class: "block text-xs font-semibold text-gray-600 mb-1" }, "Au", -1)),
                  _withDirectives(_createElementVNode("input", {
                    type: "date",
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.to) = $event)),
                    class: "border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  }, null, 512), [
                    [_vModelText, filters.to]
                  ])
                ]),
                _createElementVNode("button", {
                  onClick: applyFilters,
                  class: "px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90"
                }, "Filtrer"),
                _createElementVNode("button", {
                  onClick: resetFilters,
                  class: "px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                }, "Réinitialiser"),
                _createElementVNode("button", {
                  onClick: exportCsv,
                  disabled: exporting.value,
                  class: "ml-auto px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg text-sm hover:bg-emerald-50 flex items-center gap-2 disabled:opacity-50"
                }, [
                  _createVNode(_unref(Download), { class: "w-4 h-4" }),
                  _createTextVNode(_toDisplayString(exporting.value ? 'Export…' : 'Exporter CSV'), 1)
                ], 8, _hoisted_8)
              ]),
              (loading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, "Chargement…"))
                : (error.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(error.value), 1))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                      _createElementVNode("div", _hoisted_11, [
                        _createElementVNode("table", _hoisted_12, [
                          _cache[17] || (_cache[17] = _createElementVNode("thead", null, [
                            _createElementVNode("tr", { class: "bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide" }, [
                              _createElementVNode("th", { class: "px-4 py-3 font-semibold whitespace-nowrap" }, "Date"),
                              _createElementVNode("th", { class: "px-4 py-3 font-semibold whitespace-nowrap" }, "Profil"),
                              _createElementVNode("th", { class: "px-4 py-3 font-semibold whitespace-nowrap" }, "Catégorie"),
                              _createElementVNode("th", { class: "px-4 py-3 font-semibold" }, "Message")
                            ])
                          ], -1)),
                          _createElementVNode("tbody", null, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (s) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: s.id,
                                class: "border-t border-gray-100 align-top"
                              }, [
                                _createElementVNode("td", _hoisted_13, _toDisplayString(formatDate(s.created_at)), 1),
                                _createElementVNode("td", _hoisted_14, _toDisplayString(s.profil_label), 1),
                                _createElementVNode("td", _hoisted_15, [
                                  _createElementVNode("span", _hoisted_16, _toDisplayString(s.categorie_label), 1)
                                ]),
                                _createElementVNode("td", _hoisted_17, _toDisplayString(s.message), 1)
                              ]))
                            }), 128)),
                            (items.value.length === 0)
                              ? (_openBlock(), _createElementBlock("tr", _hoisted_18, _cache[16] || (_cache[16] = [
                                  _createElementVNode("td", {
                                    colspan: "4",
                                    class: "px-4 py-10 text-center text-gray-400"
                                  }, "Aucune suggestion.", -1)
                                ])))
                              : _createCommentVNode("", true)
                          ])
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_19, [
                        _createElementVNode("span", null, _toDisplayString(count.value) + " suggestion(s)", 1),
                        _createElementVNode("div", _hoisted_20, [
                          _createElementVNode("button", {
                            onClick: prevPage,
                            disabled: page.value <= 1,
                            class: "px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                          }, "Précédent", 8, _hoisted_21),
                          _createElementVNode("span", null, "Page " + _toDisplayString(page.value) + " / " + _toDisplayString(totalPages.value), 1),
                          _createElementVNode("button", {
                            onClick: nextPage,
                            disabled: page.value >= totalPages.value,
                            class: "px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                          }, "Suivant", 8, _hoisted_22)
                        ])
                      ])
                    ], 64))
            ]))
          : (_openBlock(), _createElementBlock("section", _hoisted_23, [
              (configLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_24, "Chargement…"))
                : (_openBlock(), _createElementBlock("form", {
                    key: 1,
                    onSubmit: _withModifiers(saveConfig, ["prevent"]),
                    class: "bg-white rounded-xl border border-gray-200 p-6 space-y-5"
                  }, [
                    _createElementVNode("div", null, [
                      _cache[18] || (_cache[18] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-1" }, "Lien de la réunion Teams", -1)),
                      _withDirectives(_createElementVNode("input", {
                        type: "url",
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.teams_url) = $event)),
                        placeholder: "https://teams.microsoft.com/l/meetup-join/…",
                        class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      }, null, 512), [
                        [_vModelText, form.teams_url]
                      ]),
                      _cache[19] || (_cache[19] = _createElementVNode("p", { class: "text-xs text-gray-400 mt-1" }, "Bouton « Parler à un technicien » du widget.", -1))
                    ]),
                    _createElementVNode("div", _hoisted_25, [
                      _createElementVNode("div", null, [
                        _cache[20] || (_cache[20] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-1" }, "Heure de début", -1)),
                        _withDirectives(_createElementVNode("input", {
                          type: "time",
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.horaire_debut) = $event)),
                          class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        }, null, 512), [
                          [_vModelText, form.horaire_debut]
                        ])
                      ]),
                      _createElementVNode("div", null, [
                        _cache[21] || (_cache[21] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-1" }, "Heure de fin", -1)),
                        _withDirectives(_createElementVNode("input", {
                          type: "time",
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.horaire_fin) = $event)),
                          class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        }, null, 512), [
                          [_vModelText, form.horaire_fin]
                        ])
                      ])
                    ]),
                    _createElementVNode("div", null, [
                      _cache[22] || (_cache[22] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-2" }, "Jours de disponibilité", -1)),
                      _createElementVNode("div", _hoisted_26, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(joursLabels, (j, idx) => {
                          return _createElementVNode("label", {
                            key: idx,
                            class: _normalizeClass(['px-3 py-1.5 rounded-lg border text-sm cursor-pointer select-none',
                  form.jours.includes(idx) ? 'border-[#22282F] bg-[#22282F]/5 text-[#22282F] font-semibold' : 'border-gray-300 text-gray-500'])
                          }, [
                            _createElementVNode("input", {
                              type: "checkbox",
                              class: "hidden",
                              value: idx,
                              checked: form.jours.includes(idx),
                              onChange: ($event: any) => (toggleJour(idx))
                            }, null, 40, _hoisted_27),
                            _createTextVNode(" " + _toDisplayString(j), 1)
                          ], 2)
                        }), 64))
                      ])
                    ]),
                    _createElementVNode("div", null, [
                      _cache[23] || (_cache[23] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-1" }, "Fuseau horaire", -1)),
                      _withDirectives(_createElementVNode("input", {
                        type: "text",
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.timezone) = $event)),
                        class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      }, null, 512), [
                        [_vModelText, form.timezone]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_28, [
                      _createElementVNode("button", {
                        type: "submit",
                        disabled: saving.value,
                        class: "px-5 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
                      }, _toDisplayString(saving.value ? 'Enregistrement…' : 'Enregistrer'), 9, _hoisted_29),
                      (configSaved.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_30, [
                            _createVNode(_unref(Check), { class: "w-4 h-4" }),
                            _cache[24] || (_cache[24] = _createTextVNode(" Enregistré "))
                          ]))
                        : _createCommentVNode("", true),
                      (configError.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_31, _toDisplayString(configError.value), 1))
                        : _createCommentVNode("", true)
                    ])
                  ], 32))
            ]))
      ])
    ]),
    _: 1
  }))
}
}

})